import { omit, sum } from "lodash";

import { client } from "@/db/db.ts";

export type DatabaseMetrics = {
  database: {
    totalMemoryBytes: number;
    backendMemoryBytes: number;
    postgresDatabaseSizeBytes: number;
    localStorageDatabaseSizeBytes: number;
    rowCount: number;
    tableFilesBytes: number;
    walFileBytes: number;
  };
  tables: Record<string, TableBreakdown>;
  settings: Record<
    string,
    {
      setting: string;
      unit: string | null;
    }
  >;
};

type TableBreakdown = {
  indexSizeBytes: number;
  rowCount: number;
  tableSizeBytes: number;
  totalSizeBytes: number;
};

export enum DatabaseHealthStatus {
  HEALTHY = "HEALTHY",
  NEEDS_SOFT_REBOOT = "NEEDS_SOFT_REBOOT",
  NEEDS_HARD_REBOOT = "NEEDS_HARD_REBOOT",
}

export class DatabaseMetricsService {
  public async collectMetrics(): Promise<DatabaseMetrics> {
    const tables = await this.tableBreakdowns();

    const settings = (await this.databaseSettings())?.reduce((memo, setting) => {
      memo[setting.name] = {
        setting: setting.setting,
        unit: setting.unit,
      };

      return memo;
    }, {});

    const database = {
      backendMemoryBytes: await this.backendMemoryBytes(),
      localStorageDatabaseSizeBytes: await this.localStorageDatabaseSizeBytes(),
      postgresDatabaseSizeBytes: await this.postgresDatabaseSizeBytes(),
      rowCount: sum(Object.values(tables).map((t) => t.rowCount)),
      tableFilesBytes: sum(Object.values(tables).map((t) => t.tableSizeBytes)),
      walFileBytes: await this.walFileBytes(),
    };

    const totalMemoryBytes =
      database.backendMemoryBytes +
      database.localStorageDatabaseSizeBytes +
      database.postgresDatabaseSizeBytes +
      database.tableFilesBytes;

    return {
      database: {
        ...database,
        totalMemoryBytes,
      },
      tables,
      settings,
    };
  }

  public async checkpoint(): Promise<void> {
    await client.query("CHECKPOINT");
  }

  public async walFileBytes(): Promise<number> {
    const bytes = parseInt(
      await this.querySingleValue<string>("SELECT SUM(size) AS wal_bytes FROM pg_ls_waldir();", "wal_bytes").catch(
        () => "0",
      ),
    );

    return Number.isNaN(bytes) ? 0 : bytes;
  }

  public async postgresDatabaseSizeBytes(): Promise<number> {
    const size = await this.querySingleValue<string>(
      "SELECT pg_database_size(current_database()) AS db_size;",
      "db_size",
    ).catch(() => null);
    return size ? parseInt(size) : 0;
  }

  public async collectCheapMetrics(): Promise<{
    backendMemoryBytes: number;
    localStorageDatabaseSizeBytes: number | null;
    postgresDatabaseSizeBytes: number;
    walFileBytes: number;
  }> {
    return {
      backendMemoryBytes: await this.backendMemoryBytes(),
      localStorageDatabaseSizeBytes: await this.localStorageDatabaseSizeBytes(),
      postgresDatabaseSizeBytes: await this.postgresDatabaseSizeBytes(),
      walFileBytes: await this.walFileBytes(),
    };
  }

  private async querySingleValue<T>(sql: string, column: string): Promise<T | undefined> {
    const res = await client.query<Record<string, T>>(sql);
    return res.rows[0]?.[column];
  }

  private async queryRows<T>(sql: string): Promise<T[]> {
    const res = await client.query<T>(sql);
    return res.rows;
  }

  private async backendMemoryBytes(): Promise<number | null> {
    const bytes = parseInt(
      await this.querySingleValue<string>(
        "SELECT SUM(total_bytes) AS total_all_backends FROM pg_backend_memory_contexts;",
        "total_all_backends",
      ).catch(() => "0"),
    );

    return Number.isNaN(bytes) ? 0 : bytes;
  }

  private async databaseSettings(): Promise<{ name: string; setting: string; unit: string | null }[]> {
    return await this.queryRows<{ name: string; setting: string; unit: string | null }>(
      "SELECT name, setting, unit FROM pg_settings WHERE name IN ('shared_buffers','work_mem','temp_buffers','maintenance_work_mem','wal_buffers','effective_cache_size');",
    ).catch(() => null);
  }

  private async localStorageDatabaseSizeBytes(): Promise<number | null> {
    // PGlite using idb:// stores data in IndexedDB; browsers expose storage estimates via navigator.storage.estimate()
    if (typeof navigator !== "undefined" && navigator.storage && navigator.storage.estimate) {
      const estimate = await navigator.storage.estimate();
      // Use usageDetails if available to approximate IndexedDB usage; otherwise use overall usage
      const idbUsage = (estimate as unknown as { usageDetails?: { indexedDB?: number } }).usageDetails?.indexedDB;
      return idbUsage ?? estimate.usage ?? null;
    }
    return null;
  }

  private async tableBreakdowns(): Promise<Record<string, TableBreakdown>> {
    const results = {};

    const tableSizes = await this.queryRows<{
      tableName: string;
      totalSizeBytes: string;
      tableSizeBytes: string;
      indexSizeBytes: string;
    }>(
      `SELECT
            relname AS "tableName",
            pg_total_relation_size(relid) AS "totalSizeBytes",
            pg_relation_size(relid) AS "tableSizeBytes",
            pg_total_relation_size(relid) - pg_relation_size(relid) AS "indexSizeBytes"
          FROM pg_catalog.pg_statio_user_tables
          WHERE schemaname = 'public'
          ORDER BY pg_total_relation_size(relid) DESC`,
    ).catch(() => []);

    for (const row of tableSizes) {
      const rowCount = await this.querySingleValue<number>(
        `select count(*) as count from public.${row.tableName}`,
        "count",
      );

      results[row.tableName] = {
        ...omit(row, "tableName"),
        rowCount,
      };
    }

    return results;
  }
}
